import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { interval, switchMap } from 'rxjs';
import type { Subscription } from 'rxjs';
import { NgpSelectPortal } from 'ng-primitives/select';
import { NgpTooltipTrigger } from 'ng-primitives/tooltip';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  heroArrowDownTray,
  heroArrowUpTray,
  heroCheck,
  heroCloudArrowUp,
  heroExclamationTriangle,
  heroInformationCircle,
  heroPlay,
  heroXMark,
} from '@ng-icons/heroicons/outline';
import {
  UiBadge,
  UiButton,
  UiDataTable,
  UiDataTableCell,
  UiEmptyState,
  UiDataTableHead,
  UiDataTableHeaderCell,
  UiDataTableRow,
  UiDataTableShell,
  UiFilePicker,
  UiHint,
  UiIconButton,
  UiLabel,
  UiPageHeader,
  UiPanelCard,
  UiPanelText,
  UiPanelTitle,
  UiRecordList,
  UiRecordRow,
  UiRecordRowBody,
  UiRecordRowDescription,
  UiRecordRowIcon,
  UiRecordRowMain,
  UiRecordRowMeta,
  UiRecordRowTitle,
  UiSelect,
  UiSelectDropdown,
  UiSelectOption,
  UiSkeletonGrid,
  UiStepNav,
  UiStepPill,
  UiStepPillBadge,
  UiStepPillLabel,
  UiTooltip,
} from '../../../shared/ui';
import { ImportsApiService } from './imports-api.service';
import {
  PRODUCTS_CANONICAL_FIELDS,
  SALES_CANONICAL_FIELDS,
  type ImportJob,
  type ImportJobRow,
  type ImportKind,
  type ImportRowStatus,
} from './imports.types';

type IssueTab = Extract<ImportRowStatus, 'failed' | 'needs_review' | 'skipped'>;
type ImportRowBadgeTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'accent';

type WizardStep = 'upload' | 'mapping' | 'progress' | 'finished';

const TERMINAL_STATUSES = ['completed', 'completed_with_errors', 'cancelled', 'failed'];

@Component({
  selector: 'app-import-wizard-page',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    NgIcon,
    NgpSelectPortal,
    NgpTooltipTrigger,
    UiBadge,
    UiButton,
    UiDataTable,
    UiDataTableCell,
    UiEmptyState,
    UiDataTableHead,
    UiDataTableHeaderCell,
    UiDataTableRow,
    UiDataTableShell,
    UiFilePicker,
    UiHint,
    UiIconButton,
    UiLabel,
    UiPageHeader,
    UiPanelCard,
    UiPanelText,
    UiPanelTitle,
    UiRecordList,
    UiRecordRow,
    UiRecordRowBody,
    UiRecordRowDescription,
    UiRecordRowIcon,
    UiRecordRowMain,
    UiRecordRowMeta,
    UiRecordRowTitle,
    UiSelect,
    UiSelectDropdown,
    UiSelectOption,
    UiSkeletonGrid,
    UiStepNav,
    UiStepPill,
    UiStepPillBadge,
    UiStepPillLabel,
    UiTooltip,
  ],
  providers: [
    provideIcons({
      heroArrowDownTray,
      heroArrowUpTray,
      heroCheck,
      heroCloudArrowUp,
      heroExclamationTriangle,
      heroInformationCircle,
      heroPlay,
      heroXMark,
    }),
  ],
  template: `
    <section class="catalog-shell">
      <app-ui-page-header
        [title]="kind() === 'sales' ? 'Importar vendas' : 'Importar produtos'"
        subtitle="Suba um arquivo CSV, mapeie as colunas e acompanhe o processamento."
        [divider]="false"
      >
        <div actions class="flex flex-wrap gap-2">
          <a appButton variant="ghost" [routerLink]="kind() === 'sales' ? '/vendas' : '/produtos'">
            <ng-icon name="heroXMark" size="14" /> Sair
          </a>
        </div>
      </app-ui-page-header>

      <nav appUiStepNav aria-label="Passos da importação">
        @for (s of stepsMeta; track s.key; let i = $index) {
          <button
            appUiStepPill
            type="button"
            tabindex="-1"
            aria-disabled="true"
            [active]="step() === s.key"
            [complete]="stepIndex(s.key) < stepIndex(step())"
          >
            <span appUiStepPillBadge>{{ i + 1 }}</span>
            <span appUiStepPillLabel>{{ s.label }}</span>
          </button>
        }
      </nav>

      <!-- ── Step 1: Upload ── -->
      @if (step() === 'upload') {
        <section appUiPanelCard class="card-panel">
          <div class="card-panel__head">
            <span appUiLabel class="mb-1 block uppercase">Passo 1</span>
            <h2 appUiPanelTitle class="mb-0 text-[1.2rem]">Selecionar arquivo</h2>
            <p appUiPanelText class="card-panel__sub">
              Aceita UTF-8 ou Windows-1252 com separador <code>,</code> ou <code>;</code>. Limite 50 MB / 50 000 linhas.
            </p>
          </div>

          <app-ui-file-picker
            class="block"
            variant="dropzone"
            icon="heroCloudArrowUp"
            accept=".csv,text/csv"
            placeholder="Arraste o CSV aqui ou clique para selecionar"
            hint="O arquivo é enviado e processado em segundo plano."
            [dragging]="dragging()"
            [disabled]="uploading()"
            (fileChange)="onFileSelected($event)"
            (dragover)="onDragOver($event)"
            (dragleave)="onDragLeave($event)"
            (drop)="onDrop($event)"
            aria-label="Arraste o CSV aqui ou clique para selecionar"
          />
          <div class="drop-zone__actions">
            <a appButton variant="secondary" [href]="api.templateUrl(kind())">
              <ng-icon name="heroArrowDownTray" size="14" /> Baixar template
            </a>
          </div>

          <details appUiPanelCard class="reference">
            <summary>📋 Ver formato esperado e campos obrigatórios</summary>
            <div appUiDataTableShell class="overflow-x-auto">
              <table appUiDataTable>
                <thead appUiDataTableHead>
                  <tr>
                    <th appUiDataTableHeaderCell>Campo</th>
                    <th appUiDataTableHeaderCell>Obrigatório?</th>
                    <th appUiDataTableHeaderCell>Valores aceitos</th>
                  </tr>
                </thead>
                <tbody>
                  @for (f of canonicalFields(); track f.key) {
                    <tr appUiDataTableRow>
                      <td appUiDataTableCell class="mono">
                        <strong>{{ f.label }}</strong>
                        @if (f.required || f.requiredOneOf) { <span class="required-mark">*</span> }
                      </td>
                      <td appUiDataTableCell>
                        @if (f.required) {
                          <span appBadge tone="danger" size="xs">Obrigatório</span>
                        } @else if (f.requiredOneOf) {
                          <span appBadge tone="warning" size="xs">Pelo menos um</span>
                        } @else {
                          <span class="cell-muted">Opcional</span>
                        }
                      </td>
                      <td appUiDataTableCell class="cell-muted">{{ f.acceptedValues || '—' }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          </details>

          @if (uploading()) {
            <p appUiHint>Enviando arquivo…</p>
          }
          @if (error()) {
            <div appUiPanelCard class="banner banner--error" role="alert">
              <ng-icon name="heroExclamationTriangle" size="16" />
              <span>{{ error() }}</span>
            </div>
          }
        </section>
      }

      <!-- ── Step 2: Mapping ── -->
      @if (step() === 'mapping' && job(); as j) {
        <section appUiPanelCard class="card-panel">
          <div class="card-panel__head">
            <span appUiLabel class="mb-1 block uppercase">Passo 2</span>
            <h2 appUiPanelTitle class="mb-0 text-[1.2rem]">Mapear colunas</h2>
            <p appUiPanelText class="card-panel__sub">Vincule cada coluna do CSV ao campo correspondente do sistema.</p>
          </div>

          @if (j.data.encoding_fallback || j.data.separator_fallback) {
            <div appUiPanelCard class="banner banner--warn" role="status">
              <ng-icon name="heroExclamationTriangle" size="16" />
              <div>
                <strong>Detectamos formato alternativo no CSV.</strong>
                <span>
                  Codificação: <code>{{ j.data.encoding_used }}</code>,
                  separador: <code>{{ j.data.separator_used }}</code>.
                </span>
              </div>
            </div>
          }

          @if (missingRequiredLabel(); as missing) {
            <div appUiPanelCard class="banner banner--error" role="alert">
              <ng-icon name="heroExclamationTriangle" size="16" />
              <div>
                <strong>Mapeamento incompleto.</strong>
                <span>Faltam: {{ missing }}.</span>
              </div>
            </div>
          }

          <details appUiPanelCard class="reference reference--mapping">
            <summary>📋 Ver formato esperado e campos obrigatórios</summary>
            <div appUiDataTableShell class="overflow-x-auto">
              <table appUiDataTable>
                <thead appUiDataTableHead>
                  <tr>
                    <th appUiDataTableHeaderCell>Campo</th>
                    <th appUiDataTableHeaderCell>Obrigatório?</th>
                    <th appUiDataTableHeaderCell>Valores aceitos</th>
                  </tr>
                </thead>
                <tbody>
                  @for (f of canonicalFields(); track f.key) {
                    <tr appUiDataTableRow>
                      <td appUiDataTableCell class="mono">
                        <strong>{{ f.label }}</strong>
                        @if (f.required || f.requiredOneOf) { <span class="required-mark">*</span> }
                      </td>
                      <td appUiDataTableCell>
                        @if (f.required) {
                          <span appBadge tone="danger" size="xs">Obrigatório</span>
                        } @else if (f.requiredOneOf) {
                          <span appBadge tone="warning" size="xs">Pelo menos um</span>
                        } @else {
                          <span class="cell-muted">Opcional</span>
                        }
                      </td>
                      <td appUiDataTableCell class="cell-muted">{{ f.acceptedValues || '—' }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          </details>

          <div appUiDataTableShell class="overflow-x-auto">
            <table appUiDataTable>
              <thead appUiDataTableHead>
                <tr>
                  <th appUiDataTableHeaderCell>Coluna do CSV</th>
                  <th appUiDataTableHeaderCell>Mapear para</th>
                  <th appUiDataTableHeaderCell>Amostra</th>
                </tr>
              </thead>
              <tbody>
                @for (col of j.data.source_columns; track col) {
                  <tr appUiDataTableRow>
                    <td appUiDataTableCell class="mono">{{ col }}</td>
                    <td appUiDataTableCell>
                      <div
                        appSelect
                        [ngpSelectValue]="mapping[col] || ''"
                        (ngpSelectValueChange)="setMapping(col, $event)"
                        attr.aria-label="Mapear coluna {{ col }}"
                      >
                        <span>{{ canonicalLabel(mapping[col]) }}</span>
                        <div *ngpSelectPortal appSelectDropdown>
                          <div appSelectOption [ngpSelectOptionValue]="''">— Ignorar coluna —</div>
                          @for (f of canonicalFields(); track f.key) {
                            <div
                              appSelectOption
                              [ngpSelectOptionValue]="f.key"
                              [attr.title]="f.acceptedValues || null"
                            >
                              {{ f.label }}
                              @if (f.required || f.requiredOneOf) {
                                <span class="required-mark">*</span>
                              }
                            </div>
                          }
                        </div>
                      </div>
                    </td>
                    <td appUiDataTableCell class="cell-muted">
                      @for (sample of sampleValues(col); track $index; let last = $last) {
                        <span class="mono-sample">{{ sample }}</span>
                        @if (!last) { <span class="dot">·</span> }
                      }
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>

          <div class="policies-grid">
            @if (kind() === 'sales') {
              <div class="form-field">
                <span appUiLabel class="inline-flex items-center gap-1 uppercase">
                  Política de ambiguidade
                  <button
                    appUiIconButton
                    variant="accent"
                    size="xs"
                    cursor="help"
                    type="button"
                    [ngpTooltipTrigger]="ambiguityTooltip"
                    [ngpTooltipTriggerShowDelay]="0"
                    [ngpTooltipTriggerHideDelay]="150"
                    aria-label="O que significa política de ambiguidade?"
                  >
                    <ng-icon name="heroInformationCircle" size="14" />
                  </button>
                </span>
                <ng-template #ambiguityTooltip>
                  <div appTooltip>
                    Define o que fazer quando o email e o telefone da linha apontam para
                    <strong>contatos diferentes</strong> no banco (dúvida de identidade).
                    Ex.: email já vinculado ao contato A, telefone já vinculado ao contato B.
                    "Pular linha" ignora; "mais antigo" usa o contato criado primeiro;
                    "ativo mais recente" usa o que está em uso há menos tempo.
                  </div>
                </ng-template>
                <div
                  appSelect
                  [ngpSelectValue]="ambiguityPolicy()"
                  (ngpSelectValueChange)="ambiguityPolicy.set($any($event))"
                  aria-label="Política de ambiguidade"
                >
                  <span>{{ ambiguityLabel() }}</span>
                  <div *ngpSelectPortal appSelectDropdown>
                    <div appSelectOption [ngpSelectOptionValue]="'skip_row'">Pular linha</div>
                    <div appSelectOption [ngpSelectOptionValue]="'pick_oldest'">Usar contato mais antigo</div>
                    <div appSelectOption [ngpSelectOptionValue]="'pick_newest_active'">Usar contato ativo mais recente</div>
                  </div>
                </div>
              </div>
              <div class="form-field">
                <span appUiLabel class="inline-flex items-center gap-1 uppercase">
                  Diferença de nome
                  <button
                    appUiIconButton
                    variant="accent"
                    size="xs"
                    cursor="help"
                    type="button"
                    [ngpTooltipTrigger]="nameDivergenceTooltip"
                    [ngpTooltipTriggerShowDelay]="0"
                    [ngpTooltipTriggerHideDelay]="150"
                    aria-label="O que significa diferença de nome?"
                  >
                    <ng-icon name="heroInformationCircle" size="14" />
                  </button>
                </span>
                <ng-template #nameDivergenceTooltip>
                  <div appTooltip>
                    Aplica quando o contato já foi identificado (sem ambiguidade), mas o
                    <strong>nome no CSV é diferente</strong> do já cadastrado.
                    Ex.: contato é "João Silva" no CRM mas o CSV traz "João Pereira".
                    "Manter cadastro" preserva o nome atual; "Atualizar com o CSV"
                    sobrescreve; "Marcar para revisão" deixa a linha pendente para você
                    decidir depois.
                  </div>
                </ng-template>
                <div
                  appSelect
                  [ngpSelectValue]="nameDivergencePolicy()"
                  (ngpSelectValueChange)="nameDivergencePolicy.set($any($event))"
                  aria-label="Política para diferença de nome"
                >
                  <span>{{ nameDivergenceLabel() }}</span>
                  <div *ngpSelectPortal appSelectDropdown>
                    <div appSelectOption [ngpSelectOptionValue]="'keep_existing'">Manter cadastro existente</div>
                    <div appSelectOption [ngpSelectOptionValue]="'update'">Atualizar com o CSV</div>
                    <div appSelectOption [ngpSelectOptionValue]="'needs_review'">Marcar linha para revisão</div>
                  </div>
                </div>
              </div>
            }
            @if (kind() === 'products') {
              <div class="form-field">
                <span appUiLabel class="uppercase">Em caso de produto duplicado</span>
                <div
                  appSelect
                  [ngpSelectValue]="onDuplicate()"
                  (ngpSelectValueChange)="onDuplicate.set($any($event))"
                  aria-label="Política para duplicatas"
                >
                  <span>{{ onDuplicateLabel() }}</span>
                  <div *ngpSelectPortal appSelectDropdown>
                    <div appSelectOption [ngpSelectOptionValue]="'skip'">Pular linha</div>
                    <div appSelectOption [ngpSelectOptionValue]="'update'">Atualizar produto</div>
                    <div appSelectOption [ngpSelectOptionValue]="'fail'">Falhar a linha</div>
                  </div>
                </div>
              </div>
            }
          </div>

          @if (error()) {
            <div appUiPanelCard class="banner banner--error" role="alert">
              <ng-icon name="heroExclamationTriangle" size="16" />
              <span>{{ error() }}</span>
            </div>
          }

          <div class="card-panel__actions">
            <button appButton variant="ghost" type="button" (click)="resetWizard()">
              Cancelar
            </button>
            <button
              appButton
              variant="primary"
              type="button"
              (click)="confirmMapping()"
              [disabled]="submitting() || !!missingRequiredLabel()"
            >
              <ng-icon name="heroPlay" size="14" />
              {{ submitting() ? 'Enviando…' : 'Iniciar importação' }}
            </button>
          </div>
        </section>
      }

      <!-- ── Step 3: Progress ── -->
      @if (step() === 'progress' && job(); as j) {
        <section appUiPanelCard class="card-panel">
          <div class="card-panel__head">
            <span appUiLabel class="mb-1 block uppercase">Passo 3</span>
            <h2 appUiPanelTitle class="mb-0 text-[1.2rem]">Processando importação</h2>
            <p appUiPanelText class="card-panel__sub">Status atual: <strong>{{ statusLabel(j.data.status) }}</strong></p>
          </div>

          <div class="progress-bar" role="progressbar" [attr.aria-valuenow]="j.data.progress_percent" aria-valuemin="0" aria-valuemax="100">
            <div class="progress-bar__fill" [style.width.%]="j.data.progress_percent"></div>
          </div>
          <p appUiHint>
            {{ j.data.progress_percent }}% — processadas {{ j.data.totals.processed }} de {{ j.data.totals.rows }}
          </p>

          <div class="stats-grid">
            <div appUiPanelCard class="stat-card stat-card--ok">
              <span appUiLabel class="uppercase">Sucesso</span>
              <span class="stat-card__value">{{ j.data.totals.succeeded }}</span>
            </div>
            <div appUiPanelCard class="stat-card stat-card--err">
              <span appUiLabel class="uppercase">Falhas</span>
              <span class="stat-card__value">{{ j.data.totals.failed }}</span>
            </div>
            <div appUiPanelCard class="stat-card stat-card--review">
              <span appUiLabel class="uppercase">Para revisar</span>
              <span class="stat-card__value">{{ j.data.totals.needs_review }}</span>
            </div>
            <div appUiPanelCard class="stat-card stat-card--skipped">
              <span appUiLabel class="uppercase">Puladas</span>
              <span class="stat-card__value">{{ j.data.totals.skipped }}</span>
            </div>
          </div>

          <div class="card-panel__actions">
            <button appButton variant="danger" type="button" (click)="cancelImport()">
              <ng-icon name="heroXMark" size="14" /> Cancelar importação
            </button>
          </div>
        </section>
      }

      <!-- ── Step 4: Finished ── -->
      @if (step() === 'finished' && job(); as j) {
        <section appUiPanelCard class="card-panel">
          <div class="card-panel__head">
            <span appUiLabel class="mb-1 block uppercase">Concluído</span>
            <h2 appUiPanelTitle class="mb-0 text-[1.2rem]">{{ finalTitle(j.data.status) }}</h2>
            <p appUiPanelText class="card-panel__sub">{{ finalSummary(j.data.status) }}</p>
          </div>

          <div class="stats-grid">
            <div appUiPanelCard class="stat-card stat-card--ok">
              <span appUiLabel class="uppercase">Sucesso</span>
              <span class="stat-card__value">{{ j.data.totals.succeeded }}</span>
            </div>
            <div appUiPanelCard class="stat-card stat-card--err">
              <span appUiLabel class="uppercase">Falhas</span>
              <span class="stat-card__value">{{ j.data.totals.failed }}</span>
            </div>
            <div appUiPanelCard class="stat-card stat-card--review">
              <span appUiLabel class="uppercase">Para revisar</span>
              <span class="stat-card__value">{{ j.data.totals.needs_review }}</span>
            </div>
            <div appUiPanelCard class="stat-card stat-card--skipped">
              <span appUiLabel class="uppercase">Puladas</span>
              <span class="stat-card__value">{{ j.data.totals.skipped }}</span>
            </div>
          </div>

          @if (hasIssues(j)) {
            <div class="issues-block">
                <div appUiStepNav class="tabs-bar" role="tablist">
                  @for (tab of issueTabs; track tab.key) {
                    <button
                      appUiStepPill
                      type="button"
                      [active]="activeIssueTab() === tab.key"
                      role="tab"
                      [attr.aria-selected]="activeIssueTab() === tab.key"
                      (click)="setIssueTab(tab.key)"
                    >
                      {{ tab.label }}
                      <span appUiStepPillBadge>{{ countFor(tab.key, j) }}</span>
                    </button>
                  }
                @if (activeIssueTab() === 'failed' && j.data.totals.failed > 0) {
                  <a
                    class="download-link"
                    appButton
                    variant="secondary"
                    size="sm"
                    [href]="api.rowsCsvUrl(j.id, 'failed')"
                  >
                    <ng-icon name="heroArrowDownTray" size="14" /> Baixar CSV de erros
                  </a>
                }
              </div>

              @if (rowsLoading()) {
                <div aria-busy="true" aria-label="Carregando linhas">
                  <app-ui-skeleton-grid [rows]="3" [rowHeight]="58" />
                </div>
              } @else if (rows().length === 0) {
                <app-ui-empty-state
                  compact
                  icon="heroInformationCircle"
                  title="Nenhuma linha neste status"
                  description="Não há itens para revisar nesta categoria."
                />
              } @else {
                <app-ui-record-list class="import-row-list">
                  @for (r of rows(); track r.id) {
                    <li appUiRecordRow [interactive]="false" class="import-row" [attr.data-status]="r.data.status">
                      <div appUiRecordRowMain>
                        <span appUiRecordRowIcon class="import-row__icon" [attr.data-status]="r.data.status" aria-hidden="true">
                          <ng-icon name="heroExclamationTriangle" size="16" />
                        </span>
                        <span appUiRecordRowBody>
                          <strong appUiRecordRowTitle>{{ r.data.error_title_pt_br || r.data.error_message || 'Sem detalhe' }}</strong>
                          <small appUiRecordRowDescription>
                            Linha {{ r.data.row_number }}
                            @if (r.data.field_label) {
                              - Campo: {{ r.data.field_label }}
                            }
                          </small>
                          @if (r.data.error_hint_pt_br) {
                            <small appUiRecordRowDescription>{{ r.data.error_hint_pt_br }}</small>
                          }
                        </span>
                      </div>
                      <div appUiRecordRowMeta>
                        <span appBadge [tone]="rowStatusTone(r.data.status)" size="xs">
                          {{ rowStatusLabel(r.data.status) }}
                        </span>
                      </div>
                      <details class="import-row__details">
                        <summary>Ver dados da linha + detalhe técnico</summary>
                        <div appUiDataTableShell class="mt-2 overflow-x-auto">
                          <table appUiDataTable>
                            <tbody>
                              @for (entry of rawRowEntries(r); track entry[0]) {
                                <tr appUiDataTableRow>
                                  <th appUiDataTableHeaderCell class="w-[35%] bg-soft/70 text-left align-top text-muted">{{ entry[0] }}</th>
                                  <td appUiDataTableCell class="text-ink">{{ entry[1] }}</td>
                                </tr>
                              }
                            </tbody>
                          </table>
                        </div>
                        @if (r.data.debug_info) {
                          <p class="import-row__debug"><strong>Debug:</strong> {{ r.data.debug_info }}</p>
                        }
                      </details>
                    </li>
                  }
                </app-ui-record-list>

                @if (rowsHasNext()) {
                  <div class="import-row-list__more">
                    <button appButton variant="secondary" size="sm" type="button" (click)="loadMoreRows()" [disabled]="rowsLoading()">
                      Carregar mais
                    </button>
                  </div>
                }
              }
            </div>
          }

          <div class="card-panel__actions">
            <button appButton variant="ghost" (click)="resetWizard()">Importar outro arquivo</button>
            <a appButton variant="primary" [routerLink]="kind() === 'sales' ? '/vendas' : '/produtos'">
              <ng-icon name="heroCheck" size="14" /> Ver {{ kind() === 'sales' ? 'vendas' : 'produtos' }}
            </a>
          </div>
        </section>
      }
    </section>
  `,
  styles: [
    `
      .catalog-shell {
        display: flex;
        flex-direction: column;
        gap: 1.25rem;
        padding-block: 1.5rem 3rem;
        padding-inline: var(--shell-pad-x);
        min-height: 100%;
      }

      .card-panel {
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }

      .card-panel__sub code {
        background: var(--color-soft);
        padding: 0 0.3rem;
        border-radius: var(--radius-xs);
        font-size: 0.78rem;
      }

      .card-panel__actions {
        display: flex;
        justify-content: flex-end;
        gap: 0.5rem;
        padding-top: 0.75rem;
        border-top: 1px solid var(--color-line);
      }

      .drop-zone__actions {
        display: flex;
        gap: 0.5rem;
        flex-wrap: wrap;
        justify-content: center;
      }

      .reference {
        display: block;
      }

      .reference > summary {
        cursor: pointer;
        font-size: 0.85rem;
        font-weight: 720;
        color: var(--color-accent-strong);
        list-style: none;
      }

      .reference[open] > summary {
        margin-bottom: 0.75rem;
      }

      .required-mark {
        color: var(--color-tomato);
        font-weight: 720;
        margin-left: 0.15rem;
      }

      .banner {
        display: flex;
        align-items: flex-start;
        gap: 0.6rem;
        font-size: 0.85rem;
        line-height: 1.4;
      }

      .banner--warn {
        color: var(--color-warning-strong);
      }

      .banner--warn strong {
        display: block;
        font-weight: 720;
      }

      .banner--warn code {
        font-size: 0.78rem;
        padding: 0 0.3rem;
        background: var(--color-panel);
        border-radius: var(--radius-xs);
      }

      .banner--error {
        color: var(--color-tomato);
      }

      .mono {
        font-variant-numeric: tabular-nums;
        font-family: var(--font-brand);
        font-size: 0.82rem;
        color: var(--color-ink);
      }

      .mono-sample {
        font-family: var(--font-brand);
        font-size: 0.75rem;
        color: var(--color-muted);
      }

      .cell-muted {
        color: var(--color-muted);
      }

      .dot {
        color: var(--color-line);
        margin: 0 0.3rem;
      }

      .policies-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(15rem, 1fr));
        gap: 0.75rem;
      }

      .form-field {
        display: flex;
        flex-direction: column;
        gap: 0.3rem;
      }

      .progress-bar {
        width: 100%;
        height: 0.55rem;
        border-radius: var(--radius-pill);
        background: var(--color-soft);
        overflow: hidden;
      }

      .progress-bar__fill {
        height: 100%;
        background: var(--color-accent);
        transition: width 320ms ease;
      }

      .stats-grid {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 0.6rem;
      }

      .stat-card {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
      }

      .stat-card__value {
        font-family: var(--font-brand);
        font-size: 1.4rem;
        font-weight: 760;
        color: var(--color-ink);
      }

      .issues-block {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
      }

      .tabs-bar {
        display: flex;
        gap: 0.4rem;
        align-items: center;
        flex-wrap: wrap;
        border-bottom: 1px solid var(--color-line);
        padding-bottom: 0.5rem;
      }

      .download-link {
        margin-left: auto;
      }

      .import-row {
        align-items: start;
      }

      .import-row__icon[data-status='failed'] {
        background: color-mix(in oklch, var(--color-tomato) 14%, var(--color-panel));
        color: var(--color-tomato);
      }

      .import-row__icon[data-status='needs_review'] {
        background: color-mix(in oklch, var(--color-warning-soft) 74%, var(--color-panel));
        color: var(--color-warning-strong);
      }

      .import-row__details {
        grid-column: 1 / -1;
        margin-top: 0.25rem;
        font-size: 0.8rem;
      }

      .import-row__details > summary {
        cursor: pointer;
        color: var(--color-accent-strong);
        font-weight: 700;
        list-style: none;
      }

      .import-row__debug {
        margin-top: 0.4rem;
        padding: 0.45rem 0.6rem;
        background: var(--color-soft);
        border-radius: var(--radius-sm);
        font-family: var(--font-brand);
        font-size: 0.72rem;
        color: var(--color-muted);
        word-break: break-all;
      }

      .import-row-list__more {
        display: flex;
        justify-content: center;
        margin-top: 0.5rem;
      }

      @media (max-width: 760px) {
        .stats-grid {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }
      }
    `,
  ],
})
export class ImportWizardPage {
  protected readonly api = inject(ImportsApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);

  readonly kind = signal<ImportKind>('sales');
  readonly step = signal<WizardStep>('upload');
  readonly job = signal<ImportJob | null>(null);
  readonly dragging = signal(false);
  readonly uploading = signal(false);
  readonly submitting = signal(false);
  readonly error = signal<string | null>(null);

  readonly stepsMeta: ReadonlyArray<{ key: WizardStep; label: string }> = [
    { key: 'upload', label: 'Upload' },
    { key: 'mapping', label: 'Mapeamento' },
    { key: 'progress', label: 'Importando' },
    { key: 'finished', label: 'Concluído' },
  ];

  mapping: Record<string, string> = {};
  readonly ambiguityPolicy = signal<'skip_row' | 'pick_oldest' | 'pick_newest_active'>('skip_row');
  readonly nameDivergencePolicy = signal<'keep_existing' | 'update' | 'needs_review'>('keep_existing');
  readonly onDuplicate = signal<'skip' | 'update' | 'fail'>('skip');

  // Issue panel (step 'finished').
  readonly issueTabs: ReadonlyArray<{ key: IssueTab; label: string }> = [
    { key: 'failed', label: 'Falhas' },
    { key: 'needs_review', label: 'Para revisar' },
    { key: 'skipped', label: 'Puladas' },
  ];
  readonly activeIssueTab = signal<IssueTab>('failed');
  readonly rows = signal<ImportJobRow[]>([]);
  readonly rowsLoading = signal(false);
  readonly rowsPage = signal(1);
  readonly rowsHasNext = signal(false);
  private readonly issuesPageSize = 50;

  readonly canonicalFields = computed(() =>
    this.kind() === 'sales' ? SALES_CANONICAL_FIELDS : PRODUCTS_CANONICAL_FIELDS,
  );

  readonly ambiguityLabel = computed(() => {
    switch (this.ambiguityPolicy()) {
      case 'pick_oldest':
        return 'Usar contato mais antigo';
      case 'pick_newest_active':
        return 'Usar contato ativo mais recente';
      default:
        return 'Pular linha';
    }
  });

  readonly nameDivergenceLabel = computed(() => {
    switch (this.nameDivergencePolicy()) {
      case 'update':
        return 'Atualizar com o CSV';
      case 'needs_review':
        return 'Marcar linha para revisão';
      default:
        return 'Manter cadastro existente';
    }
  });

  readonly onDuplicateLabel = computed(() => {
    switch (this.onDuplicate()) {
      case 'update':
        return 'Atualizar produto';
      case 'fail':
        return 'Falhar a linha';
      default:
        return 'Pular linha';
    }
  });

  constructor() {
    const kindParam = (this.route.snapshot.data['kind'] as ImportKind) ?? 'sales';
    this.kind.set(kindParam);
  }

  missingRequiredLabel(): string | null {
    const fields = this.canonicalFields();
    const mappedKeys = new Set(Object.values(this.mapping).filter((v) => !!v));

    const missingHard = fields.filter((f) => f.required && !mappedKeys.has(f.key));

    const groups = new Map<string, { label: string; satisfied: boolean }[]>();
    for (const f of fields) {
      if (!f.requiredOneOf) continue;
      const arr = groups.get(f.requiredOneOf) ?? [];
      arr.push({ label: f.label, satisfied: mappedKeys.has(f.key) });
      groups.set(f.requiredOneOf, arr);
    }

    const missingGroups: string[] = [];
    for (const items of groups.values()) {
      if (!items.some((i) => i.satisfied)) {
        missingGroups.push(items.map((i) => i.label).join(' ou '));
      }
    }

    const all = [...missingHard.map((f) => f.label), ...missingGroups];
    return all.length === 0 ? null : all.join(', ');
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragging.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragging.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragging.set(false);
    const file = event.dataTransfer?.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv') && file.type !== 'text/csv') {
      this.error.set('Apenas arquivos CSV são aceitos.');
      return;
    }
    this.uploadFile(file);
  }

  stepIndex(s: WizardStep): number {
    return this.stepsMeta.findIndex((m) => m.key === s);
  }

  canonicalLabel(key: string | undefined): string {
    if (!key) return '— Ignorar coluna —';
    const found = this.canonicalFields().find((f) => f.key === key);
    return found?.label ?? key;
  }

  sampleValues(col: string): string[] {
    const samples = this.job()?.data.sample_rows ?? [];
    return samples
      .slice(0, 3)
      .map((row) => row[col])
      .filter((v): v is string => typeof v === 'string' && v.trim().length > 0);
  }

  setMapping(col: string, value: unknown): void {
    this.mapping = { ...this.mapping, [col]: (value as string) ?? '' };
  }

  statusLabel(status: string): string {
    const map: Record<string, string> = {
      awaiting_mapping: 'Aguardando mapeamento',
      queued: 'Na fila',
      processing: 'Processando',
      completed: 'Concluída',
      completed_with_errors: 'Concluída com erros',
      cancelled: 'Cancelada',
      failed: 'Falhou',
    };
    return map[status] ?? status;
  }

  finalTitle(status: string): string {
    switch (status) {
      case 'completed':
        return 'Importação concluída';
      case 'completed_with_errors':
        return 'Concluída com erros';
      case 'cancelled':
        return 'Importação cancelada';
      case 'failed':
        return 'Importação falhou';
      default:
        return 'Importação finalizada';
    }
  }

  finalSummary(status: string): string {
    switch (status) {
      case 'completed':
        return 'Todas as linhas foram processadas com sucesso.';
      case 'completed_with_errors':
        return 'Algumas linhas falharam ou precisam de revisão. Veja o detalhe abaixo.';
      case 'cancelled':
        return 'Você cancelou a importação. As linhas já processadas permanecem.';
      case 'failed':
        return 'Falha fatal durante o processamento. Tente novamente ou contate o suporte.';
      default:
        return 'Veja os totais finais.';
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.uploadFile(file);
    // Limpa o input para permitir re-selecionar o mesmo arquivo.
    input.value = '';
  }

  private uploadFile(file: File): void {
    this.uploading.set(true);
    this.error.set(null);
    this.api
      .upload(this.kind(), file)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (job) => {
          this.job.set(job);
          this.mapping = { ...job.data.column_mapping };
          this.step.set('mapping');
          this.uploading.set(false);
        },
        error: (err) => {
          this.uploading.set(false);
          this.error.set(err?.error?.message ?? 'Falha no upload do CSV.');
        },
      });
  }

  confirmMapping(): void {
    const job = this.job();
    if (!job) return;
    this.submitting.set(true);
    this.error.set(null);
    const payload = {
      mapping: this.mapping,
      ambiguity_policy: this.kind() === 'sales' ? this.ambiguityPolicy() : undefined,
      name_divergence_policy: this.kind() === 'sales' ? this.nameDivergencePolicy() : undefined,
      on_duplicate: this.kind() === 'products' ? this.onDuplicate() : undefined,
    };
    this.api
      .confirmMapping(job.id, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.job.set(updated);
          this.step.set('progress');
          this.startPolling();
          this.submitting.set(false);
        },
        error: (err) => {
          this.submitting.set(false);
          this.error.set(err?.error?.message ?? 'Falha ao confirmar mapeamento.');
        },
      });
  }

  cancelImport(): void {
    const job = this.job();
    if (!job) return;
    this.api.cancel(job.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  resetWizard(): void {
    this.job.set(null);
    this.mapping = {};
    this.step.set('upload');
    this.error.set(null);
  }

  private pollSub: Subscription | null = null;

  private startPolling(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = interval(3000)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => this.api.getJob(this.job()!.id)),
      )
      .subscribe({
        next: (j) => {
          this.job.set(j);
          if (TERMINAL_STATUSES.includes(j.data.status)) {
            // Stop polling antes de carregar rows pra não reentrar o loop.
            this.pollSub?.unsubscribe();
            this.pollSub = null;
            if (this.step() !== 'finished') {
              this.step.set('finished');
              this.loadIssueRows(true);
            }
          }
        },
      });
  }

  // ── Issues panel ──
  hasIssues(job: ImportJob): boolean {
    const t = job.data.totals;
    return (t.failed ?? 0) + (t.needs_review ?? 0) + (t.skipped ?? 0) > 0;
  }

  countFor(tab: IssueTab, job: ImportJob): number {
    const t = job.data.totals;
    if (tab === 'failed') return t.failed ?? 0;
    if (tab === 'needs_review') return t.needs_review ?? 0;
    return t.skipped ?? 0;
  }

  setIssueTab(tab: IssueTab): void {
    if (this.activeIssueTab() === tab) return;
    this.activeIssueTab.set(tab);
    this.loadIssueRows(true);
  }

  loadMoreRows(): void {
    this.rowsPage.update((p) => p + 1);
    this.loadIssueRows(false);
  }

  private loadIssueRows(reset: boolean): void {
    const j = this.job();
    if (!j) return;
    if (reset) {
      this.rowsPage.set(1);
      this.rows.set([]);
    }
    this.rowsLoading.set(true);
    this.api
      .listRows(j.id, {
        status: this.activeIssueTab(),
        page: this.rowsPage(),
        pageSize: this.issuesPageSize,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resp) => {
          this.rows.update((arr) => (reset ? resp.data : [...arr, ...resp.data]));
          this.rowsHasNext.set(resp.meta.hasNextPage);
          this.rowsLoading.set(false);
        },
        error: () => this.rowsLoading.set(false),
      });
  }

  rawRowEntries(row: ImportJobRow): Array<[string, string]> {
    return Object.entries(row.data.raw_row ?? {});
  }

  rowStatusLabel(s: ImportRowStatus): string {
    const map: Record<ImportRowStatus, string> = {
      succeeded: 'Sucesso',
      failed: 'Falha',
      needs_review: 'Revisar',
      skipped: 'Pulada',
    };
    return map[s];
  }

  rowStatusTone(s: ImportRowStatus): ImportRowBadgeTone {
    if (s === 'failed') return 'danger';
    if (s === 'needs_review') return 'warning';
    if (s === 'succeeded') return 'success';
    return 'neutral';
  }
}
